import { useState, useEffect, SyntheticEvent, useRef, useMemo } from "react"
import { Modal } from "reactstrap"
import { toast } from "react-toastify"

import { SurveyResultsStorage } from "../../common/utils/SurveyResultsStorage"

import { Psychologist } from "utils/interfaces"
import { validateEmail } from "utils/helpers"
import { useTabletOrMobile } from "utils/hooks"
import { PersonalInfoText } from "components/PersonalInfoText"
import { PhoneInput } from "components"
import { Analytics } from "utils/analytics"
import { PsychologistAvatar } from "components/PsychologistAvatar/PsychologistAvatar"
import style from "modules/NewClientSurvey/components/ContactRequestPopup/style.module.scss"
import { Button } from "ui-kit/components-branded/index"
import { Input } from "ui-kit/components-branded/FormControls/Input/Input"
import { IS_DEVELOPMENT } from "utils/env-management"
import { UserAccount } from "utils/UserAccount"

const CLIENT_SIGNUP_STATUSES = {
    not_sent: "not_sent",
    sending: "sending",
    sent: "sent",
    error: "error",
}

interface PsychologistsAvatarsProps {
    profiles?: Psychologist[]
}

interface ContactRequestProps {
    psys: Psychologist[]
    onDone: () => void
}

function PsychologistsAvatars(props: PsychologistsAvatarsProps) {
    return (
        <ul className={style.avatars}>
            {props.profiles?.map((psy: Psychologist) => {
                return (
                    <li key={psy.hash_id} className={style.avatarsItem}>
                        <PsychologistAvatar psy={psy} size="S" />
                    </li>
                )
            })}
        </ul>
    )
}

function getStringValue(value: FormDataEntryValue | null) {
    if (value == null || typeof value !== "string") return ""
    return value
}

export const ContactRequestPopup = (props: ContactRequestProps) => {
    const formRef = useRef<HTMLFormElement>(null)
    const [status, setStatus] = useState(CLIENT_SIGNUP_STATUSES.not_sent)
    const [showErrors, setShowErrors] = useState(false)
    const [name, setName] = useState(IS_DEVELOPMENT ? "Test Test (signup form)" : "")
    const [email, setEmail] = useState(IS_DEVELOPMENT ? "test@test.com" : "")
    const [nameError, setNameError] = useState(false)
    const [emailError, setEmailError] = useState(false)
    const [phoneError, setPhoneError] = useState(false)
    const [emailValidationError, setEmailValidationError] = useState(false)
    const [emailInputIsVisible, setEmailInputIsVisible] = useState(false)
    const emailInputRef = useRef<HTMLInputElement>(null)
    const isTabletOrMobile = useTabletOrMobile()
    const [open, setOpen] = useState(!UserAccount.isSignedIn)

    useEffect(() => {
        setEmailInputIsVisible(true)
        formRef.current?.addEventListener("focusout", (event) => {
            if (event.target instanceof HTMLInputElement) {
                event.target.classList.add("is-touched")
            }
        })
    }, [])

    useEffect(() => {
        name.length === 0 && setNameError(true)
        email.length === 0 && setEmailError(true)
        setEmailValidationError(!validateEmail(email))
    }, [email, name])

    useEffect(() => {
        if (emailInputIsVisible) {
            !isTabletOrMobile && emailInputRef?.current?.focus()
        }
    }, [emailInputIsVisible, isTabletOrMobile])

    function handleSubmit(event: SyntheticEvent) {
        if (nameError || emailError || phoneError || emailValidationError) {
            setShowErrors(true)
        } else {
            Analytics.show_psy_after_form_result({ name: name, email: email })
            setStatus(CLIENT_SIGNUP_STATUSES.sending)

            const phone = formRef.current
                ? getStringValue(new FormData(formRef.current).get("phone"))
                : ""

            UserAccount.storeSignIn({
                name,
                email,
                phone,
            })

            UserAccount.signUpAfterSurvey(
                name,
                email,
                phone,
                undefined,
                SurveyResultsStorage.resultsId,
                () => {
                    props?.onDone()
                    setStatus(CLIENT_SIGNUP_STATUSES.sent)
                    toast(`
                    Мы отправили подборку на вашу почту ${email},
                    чтобы вы смогли вернуться к ней в любой момент.
                `)
                },
                (data) => {
                    if (data?.response && data.response?.error == "validation_error") {
                        if (!data.response?.fields) return
                        data.response.fields.map((fieldError) => {
                            if (fieldError.name == "name") setNameError(true)
                            if (fieldError.name == "email") setEmailError(true)
                            if (fieldError.name == "phone") setPhoneError(true)
                        })
                    }
                    setStatus(CLIENT_SIGNUP_STATUSES.error)
                }
            )
            setOpen(false)
        }

        event.preventDefault()
    }

    const content = useMemo(() => {
        if (props.psys.length === 1) {
            return {
                desktopHeadLine: "Подобрали для вас наиболее подходящего специалиста",
                mobileHeadLine: "Подобрали вам психолога",
                submitButton: "Посмотреть психолога",
            }
        }
        return {
            desktopHeadLine: "Подобрали для вас наиболее подходящих специалистов",
            mobileHeadLine: "Подобрали вам психологов",
            submitButton: "Посмотреть психологов",
        }
    }, [props.psys])

    return (
        <Modal
            className={style.modal}
            contentClassName={style.containerModal}
            backdropClassName={style.background}
            isOpen={open}
            backdropTransition={{
                baseClass: style.backgroundFade,
                timeout: 0,
            }}
        >
            <div className={style.container}>
                <div className={style.title}>
                    {isTabletOrMobile ? content.mobileHeadLine : content.desktopHeadLine}
                </div>
                <PsychologistsAvatars profiles={props.psys} />
                <div className={style.text}>
                    Результаты пришлем на&nbsp;почту. В&nbsp;смс будем присылать напоминания
                    о&nbsp;запланированных сессиях и&nbsp;контакты психологов.
                </div>
                <form onSubmit={handleSubmit} ref={formRef}>
                    <div className="mb-3">
                        <Input
                            error={
                                (emailError || emailValidationError) && showErrors
                                    ? email.length
                                        ? "Проверьте, что адрес написан без ошибок."
                                        : "Укажите вашу почту."
                                    : undefined
                            }
                            name="email"
                            placeholder="Ваша почта"
                            ref={emailInputRef}
                            value={email}
                            onChange={(value) => {
                                setEmailError(false)
                                setEmail(value)
                            }}
                        />
                    </div>
                    <div className="mb-3">
                        <PhoneInput
                            name="phone"
                            id="phone"
                            placeholder="Ваш телефон"
                            showErrors={showErrors}
                            errorCallback={(e) => setPhoneError(e)}
                            required
                            enableValidation
                        />
                    </div>
                    <div className="mb-3">
                        <Input
                            autoComplete="name"
                            error={
                                nameError && showErrors
                                    ? !name.length
                                        ? "Укажите ваше имя."
                                        : "Длина имени превышает допустимый предел"
                                    : undefined
                            }
                            isMiddleSpacesAllowed
                            name="name"
                            placeholder="Ваше имя"
                            value={name}
                            onChange={(value) => {
                                if (value && value.length < 50) {
                                    setNameError(false)
                                }
                                setName(value)
                            }}
                        />
                    </div>
                    <Button
                        type="submit"
                        color="primary"
                        isFullwidth
                        isLoading={status === CLIENT_SIGNUP_STATUSES.sending}
                        className="mb-3"
                    >
                        {content.submitButton}
                    </Button>
                    <PersonalInfoText text={content.submitButton} />
                </form>
            </div>
        </Modal>
    )
}
